import { ActivatedRoute } from '@angular/router';
import { ApiService } from './../../services/api.service';
import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { Observable, of, Subscription } from 'rxjs';
import { select } from '@angular-redux/store';
import { TranslateService, LangChangeEvent } from '@ngx-translate/core';
import { ItinActionService } from 'src/app/core/itinerari/itinerari.action';
import { Itin } from 'src/app/core/itinerari/itinerari.model';
import { Themes } from 'src/app/core/themes/themes.model';
import { BreakpointObserver } from '@angular/cdk/layout';
import { ThemePalette } from '@angular/material/core';
import { Orientation } from '@ngmodule/material-carousel';
import { catchError, first, tap } from 'rxjs/operators';
import { PaginatedItin } from 'src/app/shared/models/paginated-itin.model';

@Component({
  selector: 'app-itinerary-page',
  templateUrl: './itinerary-page.component.html',
  styleUrls: ['./itinerary-page.component.scss']
})
export class ItineraryPageComponent implements OnInit, OnDestroy {
  @ViewChild('themesFilterDesktop') themes_filter_desktop: any;
  @ViewChild('themesFilterMobile') themes_filter_mobile: any;

  themes_filter_list = [];
  search_filter_text = '';
  current_lang = 'it';

  @select(['itin', 'list'])
  itin_list$: Observable<any>;
  itin_list: Itin[] = [];
  @select(['itin', 'filtered'])
  itin_list_filtered$: Observable<any>;
  itin_list_filtered: Itin[] = [];
  @select(['itin', 'total'])
  itin_list_total$: Observable<any>;
  itin_list_total: number = 0;
  @select(['itin', 'page'])
  itin_list_page$: Observable<any>;
  itin_list_page: number = 0;
  @select(['itin', 'size'])
  itin_list_size$: Observable<any>;
  itin_list_size: number = 0;

  @select(['theme', 'list'])
  themes_list$: Observable<any>;
  themes_list: Themes[] = [];

  itinCarousel: Itin[] = [];
  subscriptions: Subscription[] = [];

  loading = false;
  loadingSearch = false;
  loadingTheme = false;

  public parentHeight = 'auto';
  public timings = '250ms ease-in';
  public autoplay = true;
  public interval = 5000;
  public loop = true;
  public hideArrows = false;
  public hideIndicators = false;
  public color: ThemePalette = 'primary';
  public maxWidth = 'auto';
  public maintainAspectRatio = false;
  public proportion = 25;
  public slideHeight = '400px';
  public slides = 10;
  public overlayColor = 'rgba(255, 255, 255, 0.7)';
  public hideOverlay = false;
  public useKeyboard = true;
  public useMouseWheel = false;
  public orientation: Orientation = 'ltr';
  public log: string[] = [];

  constructor(
    private titleService: Title,
    private itinAction: ItinActionService,
    private translate: TranslateService,
    private breakpointObserver: BreakpointObserver,
    private activatedRoute: ActivatedRoute,
    private api: ApiService
  ) {
    this.translate.setDefaultLang('it');
    if (this.translate.currentLang) {
      this.current_lang = this.translate.currentLang;
      this.translate.use(this.current_lang);
    } else {
      this.translate.use('it');
      this.current_lang = this.translate.currentLang;
    }
    this.translate.get('breadcrumb.itin').subscribe((res: string) => {
      this.titleService.setTitle(res + ' - PRIMIS');
    });
    this.translate.onLangChange.subscribe(() => {
      this.translate.get('breadcrumb.itin').subscribe((res: string) => {
        this.titleService.setTitle(res + ' - PRIMIS');
      });
    });
  }

  ngOnInit(): void {
    this.translate.onLangChange.subscribe((event: LangChangeEvent) => {
      this.changeLangFunction();
    });

    this.subscriptions.push(
      this.itin_list_filtered$.subscribe((list) => {
        this.itin_list_filtered = [...list];
      }),
      this.itin_list$.subscribe((list) => {
        this.itin_list = [...list];
      }),
      this.itin_list_page$.subscribe((page) => {
        this.itin_list_page = page;
      }),
      this.itin_list_total$.subscribe((total) => {
        this.itin_list_total = total;
      }),
      this.itin_list_size$.subscribe((size) => {
        this.itin_list_size = size;
      }),
      this.themes_list$.subscribe((list) => {
        this.themes_list = [...list];
      }),
    );

    this.activatedRoute.data.pipe(
      first(),
      tap((response: any) => {
        this.itinCarousel = [...response.carouselItin.list];
      })
    ).subscribe();

    this.breakpointObserver.observe('(max-width: 500px)').subscribe(result => {
        if (result.matches) {
          this.slideHeight = '300px';
          this.hideIndicators = true;
        } else {
          this.slideHeight = '450px';
          this.hideIndicators = false;
        }
      });
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((sub) => sub.unsubscribe());
  }

  pageChange(page: number) {
    this.getItin(page - 1, 'page', 'Non è stato possibile recuperare la nuova pagina per gli itinerari');
  }

  inputFilter(event: Event) {
    const searchItinLength = this.search_filter_text.length;
    this.search_filter_text = this.getSearchString((event.target as HTMLInputElement).value);
    if (searchItinLength !== this.search_filter_text.length) {
      this.getItin(0, 'search', `Non è stato possibile cercare ${this.search_filter_text} tra gli itinerari`);
    }
  }

  private getSearchString(searchString: string): string {
    if (!searchString || searchString?.length < 4) {
      return '';
    }
    return searchString;
  }

  themesFilterFunction(isMobile: boolean) {
    let options_selected = isMobile ?
      this.themes_filter_mobile.selectedOptions.selected :
      this.themes_filter_desktop.selectedOptions.selected;
    this.themes_filter_list = options_selected.map(({_value}) => this.getThemeIdFromName(_value, this.current_lang));
    this.getItin(0, 'theme', 'Non è stato possibile filtrare gli itinerari per percorso tematico');
  }

  private getThemeIdFromName(themeName: string, language: string): number {
    return this.themes_list.find(({name}) => name[language] === themeName).id;
  }

  changeLangFunction(): void {
    this.current_lang = this.translate.currentLang;
  }

  togglePanel(panel_id: string) {
    this.itinAction.toggleExpandPanel(panel_id);
  }

  imageUrl(dict: Itin) {
    for (const att of dict.attachments) {
      if (att.avatar) {
        return att.file_byte;
      }
    }
    return '../../../assets/home/no-picture.png';
  }

  private getItin(page: number, funcType: string, errorMsg: string) {
    this.loadingSet(funcType, true);
    this.api.getItin(page, null, this.themes_filter_list, this.search_filter_text, this.current_lang, true)
    .pipe(
      first(),
      tap((paginatedItin: PaginatedItin) => {
        this.itinAction.paginatedItinList(paginatedItin);
        this.loadingSet(funcType, false);
      }),
      catchError((err) => {
        console.error(errorMsg);
        this.loadingSet(funcType, false);
        return of(err);
      })
    ).subscribe();
  }

  private loadingSet(funcType: string, setValue: boolean) {
    switch (funcType) {
      case 'search':
        this.loadingSearch = setValue;
        break;
      case 'theme':
        this.loadingTheme = setValue;
        break;
      default:
        this.loading = setValue;
    }
  }
}
